/** Owner 成功同步后的设备数据指纹，用于 Standby→Owner 快路径跳过全量重拉 */

export type OwnerResyncSnapshot = {
  deviceKey: string;
  fingerprints: Record<string, string>;
  capturedAt: number;
};

const SNAPSHOT_KEY_PREFIX = "eweadn:ownerResyncSnapshot:";

function readEquipmentDeviceKey(): string | null {
  if (typeof window === "undefined") return null;
  try {
    const raw =
      window.sessionStorage.getItem("eweadn:session:equipmentData") ??
      window.localStorage.getItem("equipmentData");
    if (!raw) return null;
    const eq = JSON.parse(raw) as Record<string, unknown>;
    const sn = String(eq.sn ?? eq.serialNumber ?? "").trim();
    if (sn) return `sn-${sn}`;
    const vid = eq.vendorId;
    const pid = eq.productId;
    const provider = String(eq.solutionRovider ?? "");
    const name = String(eq.displayname ?? eq.productName ?? "");
    if (vid != null && pid != null && provider) {
      return `${provider}:${vid}:${pid}:${name}`;
    }
    return null;
  } catch {
    return null;
  }
}

export function readOwnerResyncDeviceKey(): string | null {
  return readEquipmentDeviceKey();
}

export function readOwnerResyncSnapshot(): OwnerResyncSnapshot | null {
  const deviceKey = readOwnerResyncDeviceKey();
  if (!deviceKey || typeof sessionStorage === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(`${SNAPSHOT_KEY_PREFIX}${deviceKey}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as OwnerResyncSnapshot;
    if (parsed?.deviceKey !== deviceKey || !parsed.fingerprints) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function writeOwnerResyncSnapshot(
  fingerprints: Record<string, string>
): void {
  const deviceKey = readOwnerResyncDeviceKey();
  if (!deviceKey || typeof sessionStorage === "undefined") return;
  try {
    const payload: OwnerResyncSnapshot = {
      deviceKey,
      fingerprints,
      capturedAt: Date.now(),
    };
    sessionStorage.setItem(
      `${SNAPSHOT_KEY_PREFIX}${deviceKey}`,
      JSON.stringify(payload)
    );
  } catch {
    /* ignore quota */
  }
}

/** 正常 load 成功后合并写入单项指纹，便于后续 Owner 快路径 */
export function mergeOwnerResyncSnapshotFingerprint(
  participantId: string,
  fingerprint: string
): void {
  const deviceKey = readOwnerResyncDeviceKey();
  if (!deviceKey || !fingerprint || typeof sessionStorage === "undefined") {
    return;
  }
  const existing = readOwnerResyncSnapshot();
  const fingerprints = {
    ...(existing?.fingerprints ?? {}),
    [participantId]: fingerprint,
  };
  writeOwnerResyncSnapshot(fingerprints);
}

/** 当前活跃 participant 的设备指纹均与快照一致 */
export function deviceFingerprintsMatchSnapshot(
  deviceFingerprints: Record<string, string>,
  participantIds: string[]
): boolean {
  const snapshot = readOwnerResyncSnapshot();
  if (!snapshot || participantIds.length === 0) return false;
  return participantIds.every((id) => {
    const current = deviceFingerprints[id];
    const saved = snapshot.fingerprints[id];
    return Boolean(current && saved && current === saved);
  });
}
